<script lang="ts" setup>
import { PlanktonPredefinedRight } from "~~/server/plankton";
import { isBellaSiciliaByDomain } from "~/utils/stores";

const { locale, t } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { HOSTING_IS_PRODUCTION: isProduction } = useRuntimeConfig().public;
const { loggedIn, user } = useUserSession();

const loginReturnUrl = computed(() => route.name?.toString().startsWith("auth-login") === false ? encodeURI(route.path) : undefined);

// Layout
const layout = await useLayout();

const loginButtonText = computed(() =>
  isBellaSiciliaByDomain() ? t("bella-sicilia.layout.header.login-button-text") : layout.value?.loginButtonText);
const registrationButtonText = computed(() =>
  isBellaSiciliaByDomain() ? t("bella-sicilia.layout.header.registration-button-text") : layout.value?.registrationButtonText);

// Mobile Menu
const isOpenMobileMenu = ref(false);
const slideoverCount = useIsSlideoverOpen();

watch(isOpenMobileMenu, (val) => {
  slideoverCount.value = val ? slideoverCount.value + 1 : Math.max(0, slideoverCount.value - 1);
});

watch(() => route.path, async () => {
  isOpenMobileMenu.value = false; // close menu when path changes, eg. /nl/catalog -> /nl/contact
});

// Basket
const { status, basket } = await useBasket(user.value, { initialize: loggedIn.value, setStatus: false });

const basketLineCount = computed(() => {
  if (!loggedIn.value) {
    return "0";
  }

  if (status.value === "pending") {
    return "";
  }

  // TODO: Plankton/NatchOS is not updating statistics?
  return basket.value?.lines?.length ?? "0"; // .statistics?.productLineCount.toString() ?? "0";
});

// Alerts
const departmentId = computed(() => user.value?.departmentId);

const alertsQuery = computed(() => ({
  loggedIn: loggedIn.value,
  departmentId: departmentId.value,
  locale: locale.value,
}));

const { data: alerts, execute: refreshAlerts } = await useFetch("/api/cms/alerts", {
  query: alertsQuery,
  immediate: false, // we will call this manually in onMounted to control the timing of the first load
});

watch([loggedIn, departmentId, locale], async () => {
  await refreshAlerts(); // refresh alerts whenever the user logs in/out, changes department, or changes locale
});

let alertsInterval: ReturnType<typeof setInterval> | undefined;

onMounted(async () => {
  await refreshAlerts(); // load alerts on mount
  alertsInterval = setInterval(refreshAlerts, 60 * 60 * 1000); // refresh alerts every hour
});

onUnmounted(() => {
  if (alertsInterval) {
    clearInterval(alertsInterval);
  }
});
</script>

<template>
  <NonProductionIndicator v-if="!isProduction" />
  <ImpersonateBanner />

  <header v-if="layout">
    <DatoCmsPreviewBanner :is-visual-editing-enabled="true" />

    <HeaderAlerts v-if="alerts" :alerts="alerts" />

    <div class="container mx-auto">
      <div class="my-md gap-md flex items-center justify-between">
        <div class="max-w-32 h-[35px] lg:max-w-48 lg:h-[52px]">
          <HeaderLogo />
        </div>

        <div class="gap-lg hidden items-center lg:flex">
          <nav class="gap-lg flex">
            <NuxtLinkLocale
              v-for="item in layout.topNavigation" :key="item.id" :to="`/${item.slug}`"
              :class="{ 'text-primary border-primary!': route.path?.toString().includes(item.slug) }"
              class="border-b-2 border-transparent text-[16px] leading-6 transition hover:border-neutral-500"
              active-class="text-primary border-primary!"
            >
              {{ item.title }}
            </NuxtLinkLocale>

            <div class="border-e-2 border-neutral-100">
              &nbsp;
            </div>

            <RequireRight :right="PlanktonPredefinedRight.CreateTemplateBasket">
              <NuxtLinkLocale
                v-if="loggedIn" to="/account/templates"
                class="border-b-2 border-transparent text-[16px] font-bold leading-6 transition hover:border-neutral-500"
                active-class="text-primary border-primary!"
              >
                {{ layout.templatesLinkText }}
              </NuxtLinkLocale>
            </RequireRight>
          </nav>

          <div v-if="!loggedIn" class="gap-md flex">
            <UButton
              variant="outline" color="primary" size="md"
              :to="localePath({ name: 'auth-login', query: { returnUrl: loginReturnUrl } })"
            >
              {{ loginButtonText }}
            </UButton>

            <div class="relative">
              <VectorWithStroke
                class="vector--main-nav-button absolute -left-0.5 -top-0.5 h-[0.9rem] w-4 rotate-55"
              />
              <UButton
                variant="solid" color="primary" size="md"
                :to="localePath(`/${layout.registrationButtonLink.slug}`)"
              >
                {{ registrationButtonText }}
              </UButton>
            </div>
          </div>

          <div v-if="loggedIn">
            <UButton
              variant="outline" color="primary" size="md"
              icon="i-ph-user" trailing :to="localePath(`/account/dashboard`)"
            >
              <span class="hidden 2xl:block">{{ layout.dashboardButtonText }}</span>
            </UButton>
          </div>

          <HeaderLocaleSwitcher />
        </div>

        <div class="gap-lg flex items-center lg:hidden">
          <div v-if="!loggedIn" class="relative">
            <VectorWithStroke class="vector--main-nav-button absolute -left-0.5 -top-0.5 h-[0.9rem] w-4 rotate-55" />
            <UButton
              variant="solid" color="primary" size="md" :ui="{ base: 'leading-6' }"
              :to="localePath({ name: 'auth-login', query: { returnUrl: loginReturnUrl } })"
            >
              {{ loginButtonText }}
            </UButton>
          </div>

          <div v-if="loggedIn">
            <UButton variant="outline" color="primary" size="md" icon="i-ph-user" trailing :to="localePath(`/account/dashboard`)" />
          </div>

          <USlideover v-model:open="isOpenMobileMenu" :dismissible="false" :ui="{ content: 'max-w-full' }" side="right">
            <div class="relative w-17.5 cursor-pointer" @click="isOpenMobileMenu = true">
              <span class="absolute bottom-0.5 right-5 z-20 bg-[#F8F7F4] pe-1.25 text-sm font-medium uppercase">Menu</span>
              <UIcon name="i-ph-list" class="relative z-10 ms-auto block size-10" />
            </div>

            <template #content>
              <div class="overflow-y-auto bg-[#F8F7F4]">
                <div class="px-md size-full">
                  <div class="my-md flex items-center justify-between">
                    <div class="w-32 lg:w-48">
                      <HeaderLogo />
                    </div>

                    <UIcon name="i-ph-x" class="size-10 cursor-pointer" @click="isOpenMobileMenu = false" />
                  </div>

                  <div class="pb-3xl">
                    <div class="my-xl gap-xl flex flex-col">
                      <div v-if="!loggedIn" class="gap-xs flex flex-col">
                        <UButton
                          variant="solid" color="primary" size="md" :ui="{ base: 'leading-6' }"
                          :to="localePath({ name: 'auth-login', query: { returnUrl: loginReturnUrl } })"
                        >
                          {{ loginButtonText }}
                        </UButton>

                        <UButton
                          variant="outline" color="primary" size="md" :ui="{ base: 'leading-6' }"
                          :to="localePath(`/${layout.registrationButtonLink.slug}`)"
                        >
                          {{ registrationButtonText }}
                        </UButton>
                      </div>

                      <nav class="gap-lg flex flex-col">
                        <NuxtLinkLocale
                          v-for="item in layout.mainNavigation" :key="item.id" :to="`/${item.slug}`"
                          :class="{ 'text-primary border-primary!': route.path?.toString().includes(item.slug) }"
                          class="text-md border-b-2 border-transparent leading-6 hover:border-neutral-500"
                          active-class="text-primary border-primary!"
                        >
                          {{ item.title }}
                        </NuxtLinkLocale>

                        <NuxtLinkLocale
                          v-for="item in layout.topNavigation" :key="item.id" :to="`/${item.slug}`"
                          :class="{ 'text-primary border-primary!': route.path?.toString().includes(item.slug) }"
                          class="text-md border-b-2 border-transparent leading-6 hover:border-neutral-500"
                          active-class="text-primary border-primary!"
                        >
                          {{ item.title }}
                        </NuxtLinkLocale>

                        <RequireRight :right="PlanktonPredefinedRight.CreateTemplateBasket">
                          <NuxtLinkLocale
                            v-if="loggedIn" to="/account/templates"
                            class="border-b-2 border-transparent font-bold leading-6 transition hover:border-neutral-500"
                            active-class="text-primary border-primary!"
                          >
                            {{ layout.templatesLinkText }}
                          </NuxtLinkLocale>
                        </RequireRight>
                      </nav>

                      <HeaderLocaleSwitcher />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </USlideover>
        </div>
      </div>
    </div>

    <div class="bg-[#F3F1EC]">
      <div class="py-sm lg:py-md container mx-auto">
        <div class="flex items-center lg:justify-between">
          <nav class="gap-x-lg hidden flex-wrap lg:flex">
            <NuxtLinkLocale
              v-for="item in layout.mainNavigation" :key="item.id" :to="`/${item.slug}`"
              :class="{ 'text-primary border-primary!': route.path?.toString().includes(item.slug) }"
              class="hover:text-primary hover:border-primary border-b-2 border-transparent text-[16px] font-semibold transition"
              active-class="text-primary border-primary!"
            >
              {{ item.title }}
            </NuxtLinkLocale>
          </nav>

          <div class="lg:gap-md flex w-full gap-[8px] lg:w-auto">
            <FormSearch mode="header" class="relative z-50 w-full" />
            <HeaderMiniBasket :basket-line-count="basketLineCount" />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style>
.vector--main-nav-button {
  --esc-vector-fill-color: #c81414;
  --esc-vector-opacity: 1;
  --esc-vector-mix-blend-mode: normal;
  --esc-vector-stroke-color: #fff;
  --esc-vector-stroke-width: 8;
}
</style>
